import { useState, useEffect, useCallback } from 'react'
import {
  typedCollection,
  onSnapshot,
  query,
  orderBy,
} from '@/lib/firebase/firestore'
import { useAuth } from '@/hooks/useAuth'
import { createUserFacingError } from '@/lib/errors'
import { adminMediaFoldersService, type MediaFolderDocument } from '../services/admin.mediaFolders.service'

export interface UseAdminMediaFoldersReturn {
  folders: MediaFolderDocument[]
  loading: boolean
  createFolder: (name: string, parentId: string | null) => Promise<string>
  renameFolder: (folderId: string, name: string) => Promise<void>
  moveFolder: (folderId: string, parentId: string | null) => Promise<void>
  deleteFolder: (folderId: string) => Promise<void>
}

export function useAdminMediaFolders(): UseAdminMediaFoldersReturn {
  const { user } = useAuth()
  const [folders, setFolders] = useState<MediaFolderDocument[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const col = typedCollection<Omit<MediaFolderDocument, 'id'>>('mediaFolders')
    const q = query(col, orderBy('name'))

    const unsub = onSnapshot(
      q,
      (snap) => {
        setFolders(snap.docs.map((d) => ({ id: d.id, ...d.data() } as MediaFolderDocument)))
        setLoading(false)
      },
      () => setLoading(false)
    )

    return unsub
  }, [])

  const createFolder = useCallback(
    async (name: string, parentId: string | null): Promise<string> => {
      if (!user) throw createUserFacingError('Please sign in before creating a folder.', 'auth/required')
      return adminMediaFoldersService.createFolder(name, parentId, user.uid)
    },
    [user]
  )

  const renameFolder = useCallback(
    (folderId: string, name: string) => adminMediaFoldersService.renameFolder(folderId, name),
    []
  )

  const moveFolder = useCallback(
    async (folderId: string, parentId: string | null) => {
      if (folderId === parentId) throw createUserFacingError('A folder cannot be moved into itself.')
      // Prevent moving into a descendant (would create a cycle)
      if (parentId) {
        const descendants = new Set<string>()
        const stack = [folderId]
        while (stack.length) {
          const cur = stack.pop()!
          for (const f of folders) {
            if (f.parentId === cur && !descendants.has(f.id)) {
              descendants.add(f.id)
              stack.push(f.id)
            }
          }
        }
        if (descendants.has(parentId)) {
          throw createUserFacingError('A folder cannot be moved into one of its subfolders.')
        }
      }
      await adminMediaFoldersService.moveFolder(folderId, parentId)
    },
    [folders]
  )

  const deleteFolder = useCallback(
    async (folderId: string) => {
      const childFolders = folders.filter((f) => f.parentId === folderId)
      if (childFolders.length > 0) {
        throw createUserFacingError("Delete the folder's subfolders before deleting this folder.")
      }
      await adminMediaFoldersService.deleteFolder(folderId)
    },
    [folders]
  )

  return { folders, loading, createFolder, renameFolder, moveFolder, deleteFolder }
}
